import { addDays } from 'date-fns';

// Utility functions for handling authentication tokens

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

/**
 * Set cookie with secure defaults
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 30)
 * @param {object} options - Additional cookie options
 */
const setCookie = (name, value, days = 30, options = {}) => {
  const expires = addDays(new Date(), days);

  const defaultOptions = {
    expires: expires.toUTCString(),
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'Lax',
  };

  const cookieOptions = { ...defaultOptions, ...options };

  let cookieString = `${name}=${value}`;
  Object.entries(cookieOptions).forEach(([key, val]) => {
    if (val !== undefined && val !== null) {
      cookieString += `; ${key}=${val}`;
    }
  });

  document.cookie = cookieString;
};

/**
 * Remove cookie
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
const removeCookie = (name, path = '/') => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
};

/**
 * Extract Bearer token from various sources
 * @returns {string|null} The access token if found, null otherwise
 */
export const extractAccessToken = () => {
  // 1. Check cookies from root domain (primary storage)
  let token = getCookie('auth_token');
  if (token) {
    return token;
  }

  // 2. Check URL query parameters (fallback - for initial auth)
  const urlParams = new URLSearchParams(window.location.search);
  token = urlParams.get('token') || urlParams.get('access_key') || urlParams.get('accessKey');
  if (token) {
    // Don't set cookie - let root domain handle it
    return token;
  }

  // 3. Check URL hash parameters (fallback - for initial auth)
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  token = hashParams.get('token') || hashParams.get('access_key') || hashParams.get('accessKey');
  if (token) {
    // Don't set cookie - let root domain handle it
    return token;
  }

  return null;
};

/**
 * Clear authentication data for all subdomains
 */
export const clearAuthData = () => {
  // Clear cookie for current domain
  removeCookie('auth_token');

  // Clear cookie for root domain (.postreams.com covers ALL subdomains)
  document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.postreams.com;';
};

// Export cookie utility functions for use in other modules
export { getCookie, setCookie, removeCookie };
